import { useState } from 'react';

export default function TradeInEstimator() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    year: '',
    make: '',
    model: '',
    trim: '',
    mileage: '',
    condition: '',
    exteriorColor: '',
    interiorColor: '',
    transmission: '',
    drivetrain: '',
    fuelType: '',
    engineSize: '',
    cylinders: '',
    vin: '',
    titleStatus: '',
    numberOfOwners: '',
    accidents: '',
    modifications: '',
    serviceHistory: '',
    tires: '',
    brakes: '',
    features: [] as string[],
    additionalInfo: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFeatureToggle = (feature: string) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.includes(feature)
        ? prev.features.filter(f => f !== feature)
        : [...prev.features, feature]
    }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    // Validate required fields
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.phone || 
        !formData.year || !formData.make || !formData.model || !formData.mileage || !formData.condition) {
      setSubmitStatus('error');
      setIsSubmitting(false);
      return;
    }

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        if (key === 'features') {
          formBody.append(key, (value as string[]).join(', '));
        } else if (value) {
          formBody.append(key, value as string);
        }
      });

      const response = await fetch('https://readdy.ai/api/form/d5s5ufnc87p5i2kb2hr0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          year: '',
          make: '',
          model: '',
          trim: '',
          mileage: '',
          condition: '',
          exteriorColor: '',
          interiorColor: '',
          transmission: '',
          drivetrain: '',
          fuelType: '',
          engineSize: '',
          cylinders: '',
          vin: '',
          titleStatus: '',
          numberOfOwners: '',
          accidents: '',
          modifications: '',
          serviceHistory: '',
          tires: '',
          brakes: '',
          features: [],
          additionalInfo: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 30 }, (_, i) => currentYear - i);

  const availableFeatures = [
    'Leather Seats',
    'Sunroof/Moonroof',
    'Navigation System',
    'Backup Camera',
    'Blind Spot Monitoring',
    'Lane Departure Warning',
    'Adaptive Cruise Control',
    'Heated Seats',
    'Cooled Seats',
    'Premium Sound System',
    'Apple CarPlay/Android Auto',
    'Keyless Entry',
    'Push Button Start',
    'Power Liftgate',
    'Third Row Seating',
    'Towing Package'
  ];

  return (
    <div className="bg-white rounded-lg shadow-lg p-8 max-w-4xl mx-auto">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-gray-900 mb-3">Trade-In Value Estimator</h2>
        <p className="text-gray-600">Get an instant estimate for your vehicle's trade-in value</p>
      </div>

      <form id="trade-in-estimator" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
        {/* Personal Information */}
        <div className="border-b border-gray-200 pb-6">
          <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
            <i className="ri-user-line mr-2 text-teal-600"></i>
            Personal Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                First Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                placeholder="John"
              />
            </div>
            <div>
              <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
                Last Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                placeholder="Doe"
              />
            </div>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                placeholder="john@example.com"
              />
            </div>
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                Phone Number <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                placeholder="(555) 123-4567"
              />
            </div>
          </div>
        </div>

        {/* Vehicle Information */}
        <div className="border-b border-gray-200 pb-6">
          <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
            <i className="ri-car-line mr-2 text-teal-600"></i>
            Vehicle Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="year" className="block text-sm font-medium text-gray-700 mb-1">
                Year <span className="text-red-500">*</span>
              </label>
              <select
                id="year"
                name="year"
                value={formData.year}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
              >
                <option value="">Select Year</option>
                {years.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="make" className="block text-sm font-medium text-gray-700 mb-1">
                Make <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="make"
                name="make"
                value={formData.make}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                placeholder="e.g., Toyota, Honda, Ford"
              />
            </div>
            <div>
              <label htmlFor="model" className="block text-sm font-medium text-gray-700 mb-1">
                Model <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="model"
                name="model"
                value={formData.model}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                placeholder="e.g., Camry, Civic, F-150"
              />
            </div>
            <div>
              <label htmlFor="trim" className="block text-sm font-medium text-gray-700 mb-1">
                Trim Level
              </label>
              <input
                type="text"
                id="trim"
                name="trim"
                value={formData.trim}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                placeholder="e.g., LE, EX, XLT"
              />
            </div>
            <div>
              <label htmlFor="mileage" className="block text-sm font-medium text-gray-700 mb-1">
                Mileage <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                id="mileage"
                name="mileage"
                value={formData.mileage}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                placeholder="e.g., 45000"
              />
            </div>
            <div>
              <label htmlFor="condition" className="block text-sm font-medium text-gray-700 mb-1">
                Condition <span className="text-red-500">*</span>
              </label>
              <select
                id="condition"
                name="condition"
                value={formData.condition}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
              >
                <option value="">Select Condition</option>
                <option value="Excellent">Excellent - Like new, no visible wear</option>
                <option value="Very Good">Very Good - Minor wear, well maintained</option>
                <option value="Good">Good - Normal wear, some minor issues</option>
                <option value="Fair">Fair - Noticeable wear, needs some repairs</option>
                <option value="Poor">Poor - Significant wear, needs major repairs</option>
              </select>
            </div>
            <div>
              <label htmlFor="vin" className="block text-sm font-medium text-gray-700 mb-1">
                VIN (Vehicle Identification Number)
              </label>
              <input
                type="text"
                id="vin"
                name="vin"
                value={formData.vin}
                onChange={handleChange}
                maxLength={17}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                placeholder="17-character VIN"
              />
            </div>
            <div>
              <label htmlFor="titleStatus" className="block text-sm font-medium text-gray-700 mb-1">
                Title Status
              </label>
              <select
                id="titleStatus"
                name="titleStatus"
                value={formData.titleStatus}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
              >
                <option value="">Select Title Status</option>
                <option value="Clean">Clean Title</option>
                <option value="Salvage">Salvage Title</option>
                <option value="Rebuilt">Rebuilt Title</option>
                <option value="Lemon">Lemon Law Buyback</option>
                <option value="Flood">Flood Damage</option>
              </select>
            </div>
          </div>
        </div>

        {/* Vehicle Details */}
        <div className="border-b border-gray-200 pb-6">
          <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
            <i className="ri-settings-3-line mr-2 text-teal-600"></i>
            Vehicle Details
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="exteriorColor" className="block text-sm font-medium text-gray-700 mb-1">
                Exterior Color
              </label>
              <input
                type="text"
                id="exteriorColor"
                name="exteriorColor"
                value={formData.exteriorColor}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                placeholder="e.g., Black, White, Silver"
              />
            </div>
            <div>
              <label htmlFor="interiorColor" className="block text-sm font-medium text-gray-700 mb-1">
                Interior Color
              </label>
              <input
                type="text"
                id="interiorColor"
                name="interiorColor"
                value={formData.interiorColor}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                placeholder="e.g., Black, Tan, Gray"
              />
            </div>
            <div>
              <label htmlFor="transmission" className="block text-sm font-medium text-gray-700 mb-1">
                Transmission
              </label>
              <select
                id="transmission"
                name="transmission"
                value={formData.transmission}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
              >
                <option value="">Select Transmission</option>
                <option value="Automatic">Automatic</option>
                <option value="Manual">Manual</option>
                <option value="CVT">CVT</option>
                <option value="Semi-Automatic">Semi-Automatic</option>
              </select>
            </div>
            <div>
              <label htmlFor="drivetrain" className="block text-sm font-medium text-gray-700 mb-1">
                Drivetrain
              </label>
              <select
                id="drivetrain"
                name="drivetrain"
                value={formData.drivetrain}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
              >
                <option value="">Select Drivetrain</option>
                <option value="FWD">Front-Wheel Drive (FWD)</option>
                <option value="RWD">Rear-Wheel Drive (RWD)</option>
                <option value="AWD">All-Wheel Drive (AWD)</option>
                <option value="4WD">Four-Wheel Drive (4WD)</option>
              </select>
            </div>
            <div>
              <label htmlFor="fuelType" className="block text-sm font-medium text-gray-700 mb-1">
                Fuel Type
              </label>
              <select
                id="fuelType"
                name="fuelType"
                value={formData.fuelType}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
              >
                <option value="">Select Fuel Type</option>
                <option value="Gasoline">Gasoline</option>
                <option value="Diesel">Diesel</option>
                <option value="Hybrid">Hybrid</option>
                <option value="Plug-in Hybrid">Plug-in Hybrid</option>
                <option value="Electric">Electric</option>
                <option value="Flex Fuel">Flex Fuel (E85)</option>
              </select>
            </div>
            <div>
              <label htmlFor="engineSize" className="block text-sm font-medium text-gray-700 mb-1">
                Engine Size
              </label>
              <input
                type="text"
                id="engineSize"
                name="engineSize"
                value={formData.engineSize}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                placeholder="e.g., 2.5L, 3.6L, 5.0L"
              />
            </div>
            <div>
              <label htmlFor="cylinders" className="block text-sm font-medium text-gray-700 mb-1">
                Number of Cylinders
              </label>
              <select
                id="cylinders"
                name="cylinders"
                value={formData.cylinders}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
              >
                <option value="">Select Cylinders</option>
                <option value="3">3 Cylinder</option>
                <option value="4">4 Cylinder</option>
                <option value="5">5 Cylinder</option>
                <option value="6">6 Cylinder</option>
                <option value="8">8 Cylinder</option>
                <option value="10">10 Cylinder</option>
                <option value="12">12 Cylinder</option>
              </select>
            </div>
            <div>
              <label htmlFor="numberOfOwners" className="block text-sm font-medium text-gray-700 mb-1">
                Number of Previous Owners
              </label>
              <select
                id="numberOfOwners"
                name="numberOfOwners"
                value={formData.numberOfOwners}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
              >
                <option value="">Select Number</option>
                <option value="1">1 Owner</option>
                <option value="2">2 Owners</option>
                <option value="3">3 Owners</option>
                <option value="4+">4+ Owners</option>
              </select>
            </div>
          </div>
        </div>

        {/* Maintenance & Condition */}
        <div className="border-b border-gray-200 pb-6">
          <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
            <i className="ri-tools-line mr-2 text-teal-600"></i>
            Maintenance &amp; Condition
          </h3>
          <div className="space-y-4">
            <div>
              <label htmlFor="accidents" className="block text-sm font-medium text-gray-700 mb-1">
                Accident History
              </label>
              <select
                id="accidents"
                name="accidents"
                value={formData.accidents}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
              >
                <option value="">Select Option</option>
                <option value="No Accidents">No Accidents</option>
                <option value="Minor Accident">Minor Accident (Repaired)</option>
                <option value="Major Accident">Major Accident (Repaired)</option>
                <option value="Multiple Accidents">Multiple Accidents</option>
              </select>
            </div>
            <div>
              <label htmlFor="serviceHistory" className="block text-sm font-medium text-gray-700 mb-1">
                Service History
              </label>
              <select
                id="serviceHistory"
                name="serviceHistory"
                value={formData.serviceHistory}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
              >
                <option value="">Select Option</option>
                <option value="Full Service Records">Full Service Records Available</option>
                <option value="Partial Service Records">Partial Service Records</option>
                <option value="No Service Records">No Service Records</option>
              </select>
            </div>
            <div>
              <label htmlFor="tires" className="block text-sm font-medium text-gray-700 mb-1">
                Tire Condition
              </label>
              <select
                id="tires"
                name="tires"
                value={formData.tires}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
              >
                <option value="">Select Condition</option>
                <option value="New">New (Less than 1 year old)</option>
                <option value="Good">Good (50%+ tread remaining)</option>
                <option value="Fair">Fair (25-50% tread remaining)</option>
                <option value="Needs Replacement">Needs Replacement Soon</option>
              </select>
            </div>
            <div>
              <label htmlFor="brakes" className="block text-sm font-medium text-gray-700 mb-1">
                Brake Condition
              </label>
              <select
                id="brakes"
                name="brakes"
                value={formData.brakes}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
              >
                <option value="">Select Condition</option>
                <option value="Recently Replaced">Recently Replaced</option>
                <option value="Good">Good Condition</option>
                <option value="Fair">Fair - Some Wear</option>
                <option value="Needs Service">Needs Service Soon</option>
              </select>
            </div>
            <div>
              <label htmlFor="modifications" className="block text-sm font-medium text-gray-700 mb-1">
                Modifications
              </label>
              <input
                type="text"
                id="modifications"
                name="modifications"
                value={formData.modifications}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                placeholder="List any aftermarket modifications"
              />
            </div>
          </div>
        </div>

        {/* Features & Options */}
        <div className="border-b border-gray-200 pb-6">
          <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
            <i className="ri-star-line mr-2 text-teal-600"></i>
            Features &amp; Options
          </h3>
          <p className="text-sm text-gray-600 mb-4">Select all features that apply to your vehicle</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {availableFeatures.map((feature) => (
              <label
                key={feature}
                className="flex items-center space-x-2 cursor-pointer p-3 border border-gray-300 rounded-md hover:bg-teal-50 hover:border-teal-500 transition-colors"
              >
                <input
                  type="checkbox"
                  checked={formData.features.includes(feature)}
                  onChange={() => handleFeatureToggle(feature)}
                  className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                />
                <span className="text-sm text-gray-700">{feature}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Additional Information */}
        <div className="pb-6">
          <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
            <i className="ri-information-line mr-2 text-teal-600"></i>
            Additional Information
          </h3>
          <div>
            <label htmlFor="additionalInfo" className="block text-sm font-medium text-gray-700 mb-1">
              Additional Comments
            </label>
            <textarea
              id="additionalInfo"
              name="additionalInfo"
              value={formData.additionalInfo}
              onChange={handleChange}
              maxLength={500}
              rows={4}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-none"
              placeholder="Any additional information about your vehicle (max 500 characters)"
            ></textarea>
            <p className="text-xs text-gray-500 mt-1">{formData.additionalInfo.length}/500 characters</p>
          </div>
        </div>

        {/* Submit Button */}
        <div className="pt-4">
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-teal-600 text-white py-3 px-6 rounded-md font-semibold hover:bg-teal-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed whitespace-nowrap"
          >
            {isSubmitting ? 'Submitting...' : 'Get My Trade-In Estimate'}
          </button>
        </div>

        {/* Status Messages */}
        {submitStatus === 'success' && (
          <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-md">
            <div className="flex items-center">
              <i className="ri-check-line text-green-600 text-xl mr-2"></i>
              <p className="text-green-800 font-medium">Thank you! Your trade-in estimate request has been submitted successfully. We'll contact you shortly with your vehicle's estimated value.</p>
            </div>
          </div>
        )}

        {submitStatus === 'error' && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-md">
            <div className="flex items-center">
              <i className="ri-error-warning-line text-red-600 text-xl mr-2"></i>
              <p className="text-red-800 font-medium">There was an error submitting your request. Please check all required fields and try again.</p>
            </div>
          </div>
        )}
      </form>

      {/* Info Box */}
      <div className="mt-8 bg-teal-50 border border-teal-200 rounded-lg p-6">
        <div className="flex items-start">
          <i className="ri-lightbulb-line text-teal-600 text-2xl mr-3 mt-1"></i>
          <div>
            <h4 className="font-semibold text-gray-900 mb-2">What happens next?</h4>
            <ul className="text-sm text-gray-700 space-y-1">
              <li>• Our team will review your vehicle information</li>
              <li>• You'll receive a preliminary estimate within 24 hours</li>
              <li>• Schedule an in-person inspection for a final offer</li>
              <li>• Get paid and drive away in your new vehicle</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
